import { useCallback, useState } from 'react'
import { NctsCountry } from 'config'
import {
  SigningSession,
  isSoftwareSigningEnabled,
  isStampitAvailable,
  isSlovakSignerAvailable,
  isTokenSigningUnavailableError,
  openSoftwareSession,
  openStampitSession,
  openSlovakSignerSession,
  openTokenSigningSession,
  openWebEidSession,
} from '../api/signingSession'

const SLOVAK_SIGNER_COUNTRIES: NctsCountry[] = ['SK', 'RS']

interface CachedSession {
  country: NctsCountry | null
  session: SigningSession
}

async function openCountrySignerSession(country: NctsCountry | null): Promise<SigningSession | null> {
  if (country && SLOVAK_SIGNER_COUNTRIES.includes(country)) {
    return (await isSlovakSignerAvailable()) ? openSlovakSignerSession() : null
  }
  if (country === 'BG') {
    return (await isStampitAvailable()) ? openStampitSession() : null
  }
  return null
}

/**
 * Manages a signing session with provider auto-detection by the declaration's country: the Slovak
 * signer on 8091 for SK and RS, StampIT on 8090 for BG, then Token Signing (legacy extension,
 * supports USB stick) and Web-eID for all of them.
 *
 * The country's local signer is probed first because running it is a deliberate act: it is on the
 * machine only if someone started it for a card no extension reaches. It also has to come first for a
 * second reason - the Web-eID extension installs `window.TokenSigning` as a compatibility shim, so the
 * Token Signing branch is entered even with nothing else installed, and a card Web-eID does not
 * recognise fails there with an error outside the unavailable set, which is rethrown rather than
 * falling through.
 *
 * The session is cached per country, so a card picked for one country never signs another's audit.
 * The caller clears it when an attempt fails, so the next attempt re-prompts - except on a 503 or a
 * network error, where retrying with the same card is the point.
 *
 * A build with REACT_APP_SOFTWARE_SIGNING takes none of those and signs with a test key instead. It
 * short-circuits rather than being another fallback: the point is to test the flow without a card, so
 * a card that happens to be plugged in must not quietly take over.
 */
function useSigningSession() {
  const [cached, setCached] = useState<CachedSession | null>(null)

  const cache = useCallback((country: NctsCountry | null, opened: SigningSession): SigningSession => {
    setCached({ country, session: opened })
    return opened
  }, [])

  const getSession = useCallback(async (country: NctsCountry | null): Promise<SigningSession> => {
    if (cached && cached.country === country) {
      return cached.session
    }

    if (isSoftwareSigningEnabled()) {
      return cache(country, await openSoftwareSession())
    }

    const localSession = await openCountrySignerSession(country)
    if (localSession) {
      return cache(country, localSession)
    }

    let newSession: SigningSession
    try {
      newSession = await openTokenSigningSession()
    } catch (err) {
      if (!isTokenSigningUnavailableError(err)) throw err
      newSession = await openWebEidSession()
    }

    // eslint-disable-next-line no-console
    console.info('[signing] provider', newSession.method)

    return cache(country, newSession)
  }, [cached, cache])

  const clearSession = useCallback(() => setCached(null), [])

  return {
    sessionCountry: cached ? cached.country : undefined,
    getSession,
    clearSession,
  }
}

export default useSigningSession
